/**
 * CLEARO "How it works" — scroll-driven timeline
 *
 * The blue line between steps fills as you scroll (and empties when you scroll
 * back up). When it reaches a step, that step gets .is-active: its number
 * lights up, the card lifts, and a squeegee wipes the fog off its photo.
 *
 * Desktop (horizontal): progress runs from when the steps enter the lower part
 * of the screen until they reach the upper third.
 * Mobile (vertical): a step activates when its number crosses the "reading
 * line" 60% down the screen, and the line fills toward the next number.
 *
 * Styles: sections/clearo-how-it-works.liquid. Disabled for reduced motion.
 */
(() => {
  if (window.clearoHow) return;
  window.clearoHow = true;

  const READING_LINE = 0.6; // mobile: fraction of viewport height
  const DESKTOP_START = 0.85; // desktop: steps' top at 85% of viewport → progress 0
  const DESKTOP_END = 0.3; // … at 30% of viewport → progress 1

  const desktop = window.matchMedia('(min-width: 990px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (v) => Math.min(1, Math.max(0, v));

  class HowTimeline {
    constructor(section) {
      this.section = section;
      this.list = section.querySelector('.clr-how__steps');
      this.steps = Array.from(section.querySelectorAll('.clr-how__step'));
      this.markers = this.steps.map((s) => s.querySelector('.clr-how__marker'));
      this.frame = null;
      this.onScroll = () => this.request();
    }

    enable() {
      if (this.enabled || !this.list || this.steps.length < 2) return;
      this.enabled = true;
      this.section.classList.add('clr-how--fx');
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      this.update();
    }

    disable() {
      if (!this.enabled) return;
      this.enabled = false;
      cancelAnimationFrame(this.frame);
      this.frame = null;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.section.classList.remove('clr-how--fx');
      this.steps.forEach((s) => {
        s.classList.remove('is-active');
        s.style.removeProperty('--fill');
      });
    }

    request() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      const vh = window.innerHeight;
      const last = this.steps.length - 1;

      if (desktop.matches) {
        // One progress value across the whole row: 0 → first step, 1 → last step
        const top = this.list.getBoundingClientRect().top;
        const p = clamp((DESKTOP_START * vh - top) / ((DESKTOP_START - DESKTOP_END) * vh));
        const pos = p * last; // e.g. 1.5 = halfway between step 2 and 3
        this.steps.forEach((step, i) => {
          step.style.setProperty('--fill', clamp(pos - i).toFixed(3));
          step.classList.toggle('is-active', p > 0 && pos >= i - 0.001);
        });
        return;
      }

      // Mobile: follow the reading line down the vertical timeline
      const line = READING_LINE * vh;
      const centers = this.markers.map((m) => {
        const r = m.getBoundingClientRect();
        return r.top + r.height / 2;
      });
      this.steps.forEach((step, i) => {
        const fill = i < last ? clamp((line - centers[i]) / (centers[i + 1] - centers[i])) : 0;
        step.style.setProperty('--fill', fill.toFixed(3));
        step.classList.toggle('is-active', centers[i] <= line);
      });
    }
  }

  const instances = new Map();

  const sync = () => {
    instances.forEach((inst) => (reducedMotion.matches ? inst.disable() : inst.enable()));
    instances.forEach((inst) => inst.enabled && inst.update());
  };

  const init = (root = document) => {
    root.querySelectorAll('.clr-how').forEach((section) => {
      if (!instances.has(section)) instances.set(section, new HowTimeline(section));
    });
    sync();
  };

  init();
  desktop.addEventListener('change', sync);
  reducedMotion.addEventListener('change', sync);

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((inst, section) => {
      if (!event.target.contains(section)) return;
      inst.disable();
      instances.delete(section);
    });
  });
})();
